// Flight recorder for one session view: every input the thread
// renders from (Reverb frames, API responses, query cache writes, thread
// actions, DOM input, viewport) and what it painted (view signatures, frame
// timing, layout shifts), timestamped so the Thread Lab can replay the view
// with its original timing. Format: sessionRecordingFormat.ts.

import {
  isCancel,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios";
import type { QueryCache } from "react-query";
import type {
  AgentChatMessage,
  QueuedPair,
} from "@/components/agent-session/types";
import { buildSha } from "@/lib/buildInfo";
import { getServerClockOffsetMs } from "@/lib/serverClock";
import { safeLocalStorage, safeSessionStorage } from "@/lib/storage";
import type { OpEntry } from "../thread/opLog";
import type { ThreadRowMeta, ThreadState } from "../thread/threadStore";
import type { StreamMessage } from "../types";
import {
  SESSION_RECORDING_FORMAT_VERSION,
  type HttpRequestLine,
  type OpTransition,
  type RecordedRow,
  type StoreCommitCause,
  type RecordingEntry,
  type RecordingHeader,
} from "./sessionRecordingFormat";

export const SESSION_RECORDING_OPT_OUT_KEY = "bs:session-recording";

const FLUSH_INTERVAL_MS = 5_000;
const CHUNK_TARGET_CHARS = 1_000_000;
const MAX_RECORDING_CHARS = 200_000_000;
const MAX_ENTRY_CHARS = 4_000_000;
const MAX_HEADER_CHARS = 16_000_000;
/** Request bodies can carry base64 attachments; replays only need their shape. */
const MAX_REQUEST_STRING_CHARS = 8_000;
const MAX_COMPOSER_VALUE_CHARS = 20_000;
/** Under the 64 KiB in-flight budget browsers give keepalive requests. */
const KEEPALIVE_MAX_BYTES = 60_000;
const FRAMES_PER_ENTRY = 120;
const COMPOSER_ID = "codesmith-chat-input";
const SESSION_SNAPSHOT_PATH = /assistant\/sessions\/[0-9a-f-]{36}$/i;

interface Segment {
  ending?: boolean;
  sessionId: string;
  recordingId: string;
  startPerf: number;
  lines: string[];
  bufferedChars: number;
  totalChars: number;
  nextChunk: number;
  view: Map<string, string>;
  viewOrder: string[];
  lastQueryData: unknown;
  lastOverride: boolean | null;
  teardown: Array<() => void>;
}

export interface ActiveSessionRecording {
  sessionId: string;
  recordingId: string;
}

let segment: Segment | null = null;
let active: ActiveSessionRecording | null = null;
/** A 401 means the backend will refuse every later recording too; a 403 is per-session (rollout is scoped to the session's org). */
let rejected = false;
let burst: number | null = null;
let nextBurst = 1;
let nextRequestId = 1;
const requestIds = new WeakMap<InternalAxiosRequestConfig, number>();
const requestSegments = new WeakMap<InternalAxiosRequestConfig, Segment>();
/** Env-var writes carry cleartext secret values. */
const SECRET_BODY_PATH = /\/env-vars(\/|$)/;
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((listener) => listener());
}

export function subscribeSessionRecording(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function isSessionRecordingEnabled(): boolean {
  return (
    !rejected &&
    typeof CompressionStream === "function" &&
    safeLocalStorage.get(SESSION_RECORDING_OPT_OUT_KEY) !== "off"
  );
}

export function setSessionRecordingEnabled(enabled: boolean): void {
  if (enabled) {
    safeLocalStorage.remove(SESSION_RECORDING_OPT_OUT_KEY);
  } else {
    safeLocalStorage.set(SESSION_RECORDING_OPT_OUT_KEY, "off");
    if (segment) end(segment, "disabled");
  }
  notify();
}

export function getActiveSessionRecording(): ActiveSessionRecording | null {
  return active;
}

export function isRecordingSession(sessionId: string): boolean {
  return segment?.sessionId === sessionId;
}

// ── Encoding ───────────────────────────────────────────────────────────────

function encode(value: unknown, clipStrings = false): string | null {
  try {
    return JSON.stringify(
      value,
      function replacer(this: Record<string, unknown>, key, current) {
        const raw = this[key];
        if (raw instanceof Date) return { $date: current };
        if (current instanceof Map) return { $map: Array.from(current) };
        if (current instanceof Set) return { $set: Array.from(current) };
        if (current instanceof URLSearchParams) {
          return { $params: current.toString() };
        }
        if (typeof Blob !== "undefined" && current instanceof Blob) {
          return { $binary: `Blob ${current.size}` };
        }
        if (typeof FormData !== "undefined" && current instanceof FormData) {
          return { $binary: "FormData" };
        }
        if (current instanceof ArrayBuffer || ArrayBuffer.isView(current)) {
          return { $binary: `bytes ${current.byteLength}` };
        }
        if (typeof current === "bigint") return current.toString();
        if (
          clipStrings &&
          typeof current === "string" &&
          current.length > MAX_REQUEST_STRING_CHARS
        ) {
          return { $clipped: current.length };
        }
        return current;
      },
    );
  } catch {
    return null;
  }
}

// ── Recording ──────────────────────────────────────────────────────────────

function record(entry: RecordingEntry, clipStrings = false): void {
  const current = segment;
  if (!current) return;
  if (burst === null) {
    burst = nextBurst++;
    queueMicrotask(() => {
      burst = null;
    });
  }
  const stamp = {
    t: Math.round((performance.now() - current.startPerf) * 100) / 100,
    burst,
  };
  let line = encode({ ...stamp, ...entry }, clipStrings);
  if (line === null || line.length > MAX_ENTRY_CHARS) {
    line = encode({
      ...stamp,
      kind: entry.kind,
      $clipped: line?.length ?? null,
    });
  }
  if (line !== null) append(current, line);
}

function append(target: Segment, line: string): void {
  target.lines.push(line);
  target.bufferedChars += line.length + 1;
  target.totalChars += line.length + 1;
  if (target.totalChars > MAX_RECORDING_CHARS && !target.ending) {
    end(target, "size-limit");
  } else if (target.bufferedChars >= CHUNK_TARGET_CHARS) {
    flush(target, false);
  }
}

function flush(target: Segment, keepalive: boolean): void {
  if (target.lines.length === 0) return;
  const body = `${target.lines.join("\n")}\n`;
  target.lines = [];
  target.bufferedChars = 0;
  void upload(target, target.nextChunk++, body, keepalive);
}

async function gzip(text: string): Promise<ArrayBuffer> {
  const compressed = new Response(text).body!.pipeThrough(
    new CompressionStream("gzip"),
  );
  return new Response(compressed).arrayBuffer();
}

async function upload(
  target: Segment,
  index: number,
  body: string,
  keepalive: boolean,
): Promise<void> {
  if (rejected) return;
  const token = safeSessionStorage.get("fa-test-token");
  let status: number | null = null;
  try {
    const bytes = await gzip(body);
    // No Content-Type keeps this a CORS-simple request: keepalive fetches
    // cannot be preflighted.
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_API_URL}/api/assistant/sessions/${target.sessionId}/recordings/${target.recordingId}/chunks/${index}`,
      {
        method: "POST",
        credentials: "include",
        keepalive: keepalive && bytes.byteLength <= KEEPALIVE_MAX_BYTES,
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: bytes,
      },
    );
    if (response.ok) return;
    status = response.status;
  } catch {
    // Network failure: this chunk is lost; later ones may still land.
  }
  if (status !== null && status >= 400 && status < 500) {
    if (status === 401) rejected = true;
    console.warn(
      `[session-recording] Upload rejected, recording stopped: session=${target.sessionId} recording=${target.recordingId} status=${status}`,
    );
    if (segment === target) end(target, `upload-rejected:${status}`);
    notify();
    return;
  }
  if (segment === target) record({ kind: "chunk-lost", index });
}

function end(target: Segment, reason: string): void {
  if (segment !== target || target.ending) return;
  target.ending = true;
  target.teardown.forEach((teardown) => teardown());
  record({ kind: "end", reason });
  segment = null;
  active = null;
  flush(target, reason === "pagehide");
  notify();
}

export interface BeginSessionRecordingInit {
  sessionId: string;
  cachedSession: unknown;
  threadState: unknown;
  wsConnection: string | null;
  user: Record<string, unknown>;
  queryCache: QueryCache;
}

/** Starts recording `sessionId`'s view; returns the matching stop. */
export function beginSessionRecording(
  init: BeginSessionRecordingInit,
): () => void {
  if (segment) end(segment, "replaced");
  const target: Segment = {
    sessionId: init.sessionId,
    recordingId: crypto.randomUUID(),
    startPerf: performance.now(),
    lines: [],
    bufferedChars: 0,
    totalChars: 0,
    nextChunk: 0,
    view: new Map(),
    viewOrder: [],
    lastQueryData: init.cachedSession,
    lastOverride: null,
    teardown: [],
  };
  const header: RecordingHeader = {
    kind: "header",
    version: SESSION_RECORDING_FORMAT_VERSION,
    recordingId: target.recordingId,
    sessionId: init.sessionId,
    epochMs: Date.now(),
    timeOrigin: performance.timeOrigin,
    buildSha: buildSha(),
    revealEnv: {
      wordRevealMs: process.env.NEXT_PUBLIC_AGENT_WORD_REVEAL_MS ?? null,
      fallbackMs: process.env.NEXT_PUBLIC_AGENT_REVEAL_FALLBACK_MS ?? null,
    },
    href: window.location.href,
    userAgent: navigator.userAgent,
    viewport: viewport(),
    reducedMotion:
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false,
    darkMode: document.documentElement.classList.contains("dark"),
    visibility: document.visibilityState,
    focused: document.hasFocus(),
    serverClockOffsetMs: getServerClockOffsetMs(),
    wsConnection: init.wsConnection,
    storage: snapshotStorage(init.sessionId),
    user: init.user,
    cachedSession: init.cachedSession,
    threadState: init.threadState,
  };
  let headerLine = encode(header);
  if (headerLine === null || headerLine.length > MAX_HEADER_CHARS) {
    const clipped = { $clipped: headerLine?.length ?? null };
    headerLine = encode({
      ...header,
      cachedSession: clipped,
      threadState: clipped,
    });
  }
  segment = target;
  active = { sessionId: init.sessionId, recordingId: target.recordingId };
  if (headerLine !== null) append(target, headerLine);

  const interval = setInterval(() => flush(target, false), FLUSH_INTERVAL_MS);
  target.teardown.push(
    () => clearInterval(interval),
    tapQueryCache(target, init.queryCache),
    tapDom(target),
  );
  notify();
  return () => end(target, "closed");
}

/** Records a viewer-flagged moment and uploads everything buffered so far. */
export function markSessionRecording(label: string): boolean {
  const current = segment;
  if (!current) return false;
  record({ kind: "mark", label });
  flush(current, false);
  return true;
}

// ── Taps called from the app's choke points ────────────────────────────────

export function recordWsFrame(
  channel: string,
  event: string,
  data: unknown,
): void {
  record({ kind: "ws", channel, event, data });
}

export function recordWsLifecycle(
  channel: string,
  phase: "subscribe" | "subscribed" | "error" | "leave",
  error?: unknown,
): void {
  record({
    kind: "ws-lifecycle",
    channel,
    phase,
    ...(error === undefined
      ? {}
      : {
          error:
            error instanceof Error
              ? { name: error.name, message: error.message }
              : error,
        }),
  });
}

export function recordWsConnection(previous: string, current: string): void {
  record({ kind: "ws-connection", previous, current });
}

export function recordWire(
  sessionId: string,
  entry: Extract<RecordingEntry, { kind: "wire" }>,
): void {
  if (isRecordingSession(sessionId)) record(entry);
}

function recordedRow(
  id: string,
  row: StreamMessage,
  meta: ThreadRowMeta | undefined,
): RecordedRow {
  return {
    id,
    role: row.role,
    status: row.status ?? null,
    serverConfirmed: meta?.serverConfirmed ?? false,
  };
}

function diffOps(previous: OpEntry[], next: OpEntry[]): OpTransition[] {
  const before = new Map(previous.map((op) => [op.id, op]));
  const transitions: OpTransition[] = [];
  for (const op of next) {
    const was = before.get(op.id);
    before.delete(op.id);
    if (was?.state === op.state) continue;
    transitions.push({
      id: op.id,
      type: op.type,
      from: was?.state ?? null,
      to: op.state,
      ...(op.lastError ? { error: op.lastError } : {}),
    });
  }
  before.forEach((op) =>
    transitions.push({ id: op.id, type: op.type, from: op.state, to: null }),
  );
  return transitions;
}

function pinKey(meta: ThreadRowMeta | undefined): string {
  return JSON.stringify(meta?.pin ?? null);
}

/** Records what one threads-store commit changed for the recorded session. */
export function recordStoreCommit(
  sessionId: string,
  previous: ThreadState,
  next: ThreadState,
  cause: StoreCommitCause,
): void {
  if (!isRecordingSession(sessionId)) return;
  const ops = previous.ops === next.ops ? [] : diffOps(previous.ops, next.ops);
  const added: RecordedRow[] = [];
  const removed: RecordedRow[] = [];
  const status: Array<{ id: string; from: string | null; to: string | null }> =
    [];
  if (previous.rows !== next.rows) {
    previous.rows.forEach((row, id) => {
      if (!next.rows.has(id)) {
        removed.push(recordedRow(id, row, previous.meta.get(id)));
      }
    });
    next.rows.forEach((row, id) => {
      const was = previous.rows.get(id);
      if (!was) {
        added.push(recordedRow(id, row, next.meta.get(id)));
      } else if (was.status !== row.status) {
        status.push({ id, from: was.status ?? null, to: row.status ?? null });
      }
    });
  }
  const pins: Array<{ id: string; from: unknown; to: unknown }> = [];
  if (previous.meta !== next.meta) {
    next.meta.forEach((meta, id) => {
      const was = previous.meta.get(id);
      if (pinKey(was) !== pinKey(meta)) {
        pins.push({ id, from: was?.pin ?? null, to: meta.pin });
      }
    });
  }
  const hiddenAdded = [...next.hiddenIds].filter(
    (id) => !previous.hiddenIds.has(id),
  );
  const hiddenRemoved = [...previous.hiddenIds].filter(
    (id) => !next.hiddenIds.has(id),
  );
  const resynced = previous.resyncEpoch !== next.resyncEpoch;
  if (
    ops.length === 0 &&
    added.length === 0 &&
    removed.length === 0 &&
    status.length === 0 &&
    pins.length === 0 &&
    hiddenAdded.length === 0 &&
    hiddenRemoved.length === 0 &&
    !resynced
  ) {
    return;
  }
  record({
    kind: "store",
    cause,
    ...(ops.length > 0 ? { ops } : {}),
    ...(added.length > 0 ? { added } : {}),
    ...(removed.length > 0 ? { removed } : {}),
    ...(status.length > 0 ? { status } : {}),
    ...(pins.length > 0 ? { pins } : {}),
    ...(hiddenAdded.length > 0 || hiddenRemoved.length > 0
      ? { hidden: { added: hiddenAdded, removed: hiddenRemoved } }
      : {}),
    ...(resynced ? { resyncEpoch: next.resyncEpoch } : {}),
  });
}

export function recordSessionAction(
  sessionId: string,
  name: string,
  args: unknown[],
  result: unknown,
  op: unknown,
): void {
  if (!isRecordingSession(sessionId)) return;
  const handle = result as {
    opId?: string;
    userMessageId?: string;
    draftMessageId?: string;
  } | null;
  record(
    {
      kind: "action",
      name,
      args,
      result: handle
        ? {
            opId: handle.opId,
            userMessageId: handle.userMessageId,
            draftMessageId: handle.draftMessageId,
          }
        : null,
      op: op ?? null,
    },
    true,
  );
}

function viewSignature(message: AgentChatMessage): string {
  const status = message.isStreaming
    ? "streaming"
    : message.isQueued
      ? "queued"
      : message.isProcessing
        ? "processing"
        : message.isInterrupted
          ? "interrupted"
          : "settled";
  const parts =
    message.turns?.reduce((count, turn) => count + turn.parts.length, 0) ?? 0;
  return `${message.role}|${status}|${message.toolEvents?.length ?? 0}|${parts}|${message.content.length}`;
}

/** ChatPane's rows keyed by id, in view order; `view` entries diff these. */
function sessionViewSignatures(
  activeMessages: AgentChatMessage[],
  queuedMessages: QueuedPair[],
): Map<string, string> {
  const signatures = new Map<string, string>();
  for (const message of activeMessages) {
    signatures.set(message.id, viewSignature(message));
  }
  for (const pair of queuedMessages) {
    signatures.set(
      `queued:${pair.userMessage.id}`,
      `queued|${pair.sendFailed ? "failed" : "pending"}|${pair.userMessage.content.length}`,
    );
  }
  return signatures;
}

const viewObservers = new Set<(signatures: Map<string, string>) => void>();

/** Thread Lab replay compares what SessionThreadPane renders against a recording's `view` entries. */
export function observeSessionViews(
  observer: (signatures: Map<string, string>) => void,
): () => void {
  viewObservers.add(observer);
  return () => viewObservers.delete(observer);
}

export function recordSessionView(
  activeMessages: AgentChatMessage[],
  queuedMessages: QueuedPair[],
  overrideAccepted: () => boolean,
): void {
  const current = segment;
  if (!current && viewObservers.size === 0) return;
  const next = sessionViewSignatures(activeMessages, queuedMessages);
  viewObservers.forEach((observer) => observer(next));
  if (!current) return;
  const override = overrideAccepted();
  const overrideChanged = override !== current.lastOverride;
  current.lastOverride = override;
  const order = Array.from(next.keys());
  const rows: Record<string, string> = {};
  next.forEach((signature, id) => {
    if (current.view.get(id) !== signature) rows[id] = signature;
  });
  const removed = current.viewOrder.filter((id) => !next.has(id));
  const orderChanged =
    order.length !== current.viewOrder.length ||
    order.some((id, index) => current.viewOrder[index] !== id);
  current.view = next;
  current.viewOrder = order;
  if (!orderChanged && !overrideChanged && Object.keys(rows).length === 0) {
    return;
  }
  record({
    kind: "view",
    ...(overrideChanged ? { override } : {}),
    ...(orderChanged ? { order } : {}),
    ...(Object.keys(rows).length > 0 ? { rows } : {}),
    ...(removed.length > 0 ? { removed } : {}),
  });
}

const REDACTED = { $redacted: true } as const;

/**
 * Only the recorded session's own requests keep their query, params and
 * bodies: others (e.g. `?search=` from ⌘K) carry text typed outside the
 * composer, which input entries mask.
 */
function isRecordedSessionRequest(
  config: InternalAxiosRequestConfig,
  target: Segment,
): boolean {
  const url = config.url ?? "";
  return url.includes(target.sessionId) && !SECRET_BODY_PATH.test(url);
}

function describeRequest(
  config: InternalAxiosRequestConfig,
  target: Segment,
): HttpRequestLine {
  const method = (config.method ?? "get").toUpperCase();
  const url = config.url ?? "";
  if (isRecordedSessionRequest(config, target)) {
    return { method, url, params: config.params };
  }
  const query = url.includes("?") || config.params !== undefined;
  return {
    method,
    url: url.split("?")[0],
    ...(query ? { params: REDACTED } : {}),
  };
}

function recordedBody(
  config: InternalAxiosRequestConfig,
  target: Segment,
  body: unknown,
): unknown {
  return body === undefined || isRecordedSessionRequest(config, target)
    ? body
    : REDACTED;
}

function isOwnTelemetry(config: InternalAxiosRequestConfig): boolean {
  return (config.url ?? "").includes("frontend/");
}

/** Records every request through `client` while a session is being recorded. */
export function instrumentApiClientForRecording(client: AxiosInstance): void {
  client.interceptors.request.use((config) => {
    const target = segment;
    if (!target || isOwnTelemetry(config)) return config;
    const id = nextRequestId++;
    requestIds.set(config, id);
    requestSegments.set(config, target);
    record(
      {
        kind: "http",
        phase: "request",
        id,
        ...describeRequest(config, target),
        data: recordedBody(config, target, config.data),
      },
      true,
    );
    return config;
  });
  client.interceptors.response.use(
    (response) => {
      const config = response.config;
      const target = segment;
      if (!target || requestSegments.get(config) !== target) return response;
      const request = describeRequest(config, target);
      const isSnapshot =
        request.method === "GET" &&
        SESSION_SNAPSHOT_PATH.test(request.url) &&
        !(config.params as { status_only?: unknown } | undefined)?.status_only;
      record({
        kind: "http",
        phase: "response",
        id: requestIds.get(config) ?? null,
        ...request,
        status: response.status,
        data: isSnapshot
          ? { $see: "query" }
          : recordedBody(config, target, response.data),
      });
      return response;
    },
    (error) => {
      const config: InternalAxiosRequestConfig | undefined = error?.config;
      const target = segment;
      if (target && config && requestSegments.get(config) === target) {
        record({
          kind: "http",
          phase: "error",
          id: requestIds.get(config) ?? null,
          ...describeRequest(config, target),
          status: error?.response?.status ?? null,
          error: isCancel(error)
            ? "cancelled"
            : String(error?.code ?? error?.message ?? error),
          data: recordedBody(config, target, error?.response?.data),
        });
      }
      return Promise.reject(error);
    },
  );
}

// ── Taps owned by the segment ──────────────────────────────────────────────

function tapQueryCache(target: Segment, queryCache: QueryCache): () => void {
  return queryCache.subscribe((event) => {
    if (segment !== target || event?.type !== "queryUpdated") return;
    if (event.action.type !== "success") return;
    const key = event.query.queryKey as unknown[];
    if (key[0] !== "assistantSession" || key[1] !== target.sessionId) return;
    const data = event.query.state.data;
    if (data === target.lastQueryData) {
      record({ kind: "query", key, unchanged: true });
      return;
    }
    target.lastQueryData = data;
    record({ kind: "query", key, data });
  });
}

function viewport() {
  return {
    width: window.innerWidth,
    height: window.innerHeight,
    dpr: window.devicePixelRatio,
  };
}

function snapshotStorage(sessionId: string): RecordingHeader["storage"] {
  const local: Record<string, string> = {};
  const session: Record<string, string> = {};
  const localKeys = [
    ...safeLocalStorage.getKeysWithPrefix("bs:"),
    "codesmith-chat-experiments",
    "theme",
    `codesmith.opLog.v1:${sessionId}`,
  ];
  for (const key of localKeys) {
    const value = safeLocalStorage.get(key);
    if (value !== null) local[key] = value;
  }
  for (const key of safeSessionStorage.getKeysWithPrefix("bs:")) {
    const value = safeSessionStorage.get(key);
    if (value !== null) session[key] = value;
  }
  return { local, session };
}

function describeTarget(target: EventTarget | null): string {
  if (target === document) return "document";
  if (target === window) return "window";
  const element =
    target instanceof Element
      ? target
      : target instanceof Node
        ? target.parentElement
        : null;
  if (!element) return "?";
  let description = element.tagName.toLowerCase();
  if (element.id) description += `#${element.id}`;
  const testId = element.getAttribute("data-testid");
  if (testId) description += `[data-testid=${testId}]`;
  if (element.hasAttribute("data-transcript-scroller")) {
    description += "[data-transcript-scroller]";
  }
  const label = element.getAttribute("aria-label");
  if (label) description += `[aria-label=${label.slice(0, 60)}]`;
  return description;
}

function isComposer(target: EventTarget | null): boolean {
  return target instanceof Element && target.id === COMPOSER_ID;
}

function rect(r: DOMRectReadOnly): [number, number, number, number] {
  return [r.x, r.y, r.width, r.height].map(
    (value) => Math.round(value * 10) / 10,
  ) as [number, number, number, number];
}

interface LayoutShiftEntry extends PerformanceEntry {
  value: number;
  hadRecentInput: boolean;
  sources: Array<{
    node: Node | null;
    previousRect: DOMRectReadOnly;
    currentRect: DOMRectReadOnly;
  }>;
}

interface LongAnimationFrameEntry extends PerformanceEntry {
  blockingDuration: number;
}

function tapDom(target: Segment): () => void {
  const teardown: Array<() => void> = [];
  const relative = (ms: number) =>
    Math.round((ms - target.startPerf) * 100) / 100;
  const listen = (
    on: Window | Document,
    type: string,
    listener: (event: Event) => void,
    options?: AddEventListenerOptions,
  ) => {
    on.addEventListener(type, listener, options);
    teardown.push(() => on.removeEventListener(type, listener, options));
  };
  const capture = { capture: true, passive: true };

  let frameStart: number | null = null;
  let lastFrame: number | null = null;
  let deltas: number[] = [];
  const emitFrames = () => {
    if (frameStart !== null && deltas.length > 0) {
      record({ kind: "frames", start: relative(frameStart), deltas });
    }
    frameStart = lastFrame;
    deltas = [];
  };
  let frame = requestAnimationFrame(function tick(now) {
    if (lastFrame === null) {
      frameStart = now;
    } else {
      deltas.push(Math.round((now - lastFrame) * 10) / 10);
    }
    lastFrame = now;
    if (deltas.length >= FRAMES_PER_ENTRY) emitFrames();
    frame = requestAnimationFrame(tick);
  });
  teardown.push(() => {
    cancelAnimationFrame(frame);
    emitFrames();
  });

  listen(window, "resize", () => record({ kind: "viewport", ...viewport() }));
  listen(window, "focus", () => record({ kind: "focus", focused: true }));
  listen(window, "blur", () => record({ kind: "focus", focused: false }));
  listen(document, "visibilitychange", () => {
    record({ kind: "visibility", state: document.visibilityState });
    if (document.visibilityState === "hidden") {
      emitFrames();
      flush(target, true);
    }
  });
  listen(window, "pagehide", () => end(target, "pagehide"));

  listen(
    document,
    "wheel",
    (event) => {
      const wheel = event as WheelEvent;
      record({
        kind: "input",
        type: "wheel",
        target: describeTarget(wheel.target),
        dx: wheel.deltaX,
        dy: wheel.deltaY,
        mode: wheel.deltaMode,
      });
    },
    capture,
  );
  for (const type of ["pointerdown", "pointerup"] as const) {
    listen(
      document,
      type,
      (event) => {
        const pointer = event as PointerEvent;
        record({
          kind: "input",
          type,
          target: describeTarget(pointer.target),
          x: pointer.clientX,
          y: pointer.clientY,
          button: pointer.button,
          pointerType: pointer.pointerType,
        });
      },
      capture,
    );
  }
  listen(
    document,
    "keydown",
    (event) => {
      const key = event as KeyboardEvent;
      const chord = key.altKey || key.ctrlKey || key.metaKey;
      const reveal = isComposer(key.target) || key.key.length > 1 || chord;
      record({
        kind: "input",
        type: "keydown",
        target: describeTarget(key.target),
        key: reveal ? key.key : "*",
        code: reveal ? key.code : undefined,
        alt: key.altKey,
        ctrl: key.ctrlKey,
        meta: key.metaKey,
        shift: key.shiftKey,
        repeat: key.repeat,
      });
    },
    capture,
  );
  listen(
    document,
    "input",
    (event) => {
      const field = event.target as HTMLInputElement | HTMLTextAreaElement;
      const value = typeof field.value === "string" ? field.value : "";
      record({
        kind: "input",
        type: "input",
        target: describeTarget(field),
        ...(isComposer(field)
          ? { value: value.slice(0, MAX_COMPOSER_VALUE_CHARS) }
          : { length: value.length }),
      });
    },
    capture,
  );
  listen(
    document,
    "scroll",
    (event) => {
      const element = event.target;
      if (!(element instanceof Element)) return;
      record({
        kind: "scroll",
        target: describeTarget(element),
        top: Math.round(element.scrollTop * 10) / 10,
        height: element.scrollHeight,
        client: element.clientHeight,
      });
    },
    capture,
  );

  const observe = (
    type: string,
    onEntry: (entry: PerformanceEntry) => void,
  ) => {
    if (
      typeof PerformanceObserver === "undefined" ||
      !PerformanceObserver.supportedEntryTypes?.includes(type)
    ) {
      return;
    }
    const observer = new PerformanceObserver((list) =>
      list.getEntries().forEach(onEntry),
    );
    observer.observe({ type, buffered: false });
    teardown.push(() => observer.disconnect());
  };
  observe("layout-shift", (entry) => {
    const shift = entry as LayoutShiftEntry;
    record({
      kind: "layout-shift",
      start: relative(shift.startTime),
      value: shift.value,
      hadRecentInput: shift.hadRecentInput,
      sources: shift.sources.map((source) => ({
        node: describeTarget(source.node),
        previous: rect(source.previousRect),
        current: rect(source.currentRect),
      })),
    });
  });
  observe("long-animation-frame", (entry) => {
    record({
      kind: "long-frame",
      start: relative(entry.startTime),
      duration: Math.round(entry.duration),
      blocking: Math.round((entry as LongAnimationFrameEntry).blockingDuration),
    });
  });

  return () => teardown.forEach((stop) => stop());
}
